import { useQuery } from '@tanstack/react-query';

import { api } from '@/shared/lib/axios';

import { meResponseSchema, type User } from '../types';
import { useAuth } from '../use-auth';

export const ME_QUERY_KEY = ['auth', 'me'] as const;

async function fetchMe(): Promise<User> {
  const res = await api.get('/api/auth/me');
  return meResponseSchema.parse(res.data).user;
}

// TanStack Query hook for the current user. AuthContext seeds initialData on login,
// so consumers see the user instantly without a redundant network call. The query is
// disabled until login has populated AuthContext (avoids a guaranteed-401 fetch on
// the unauthenticated paths). When api/'s /me is ready, flip this to enabled: true
// or set staleTime: 0 to force a fresh fetch on every mount.

export function useMe() {
  const { user: contextUser } = useAuth();
  return useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: fetchMe,
    initialData: contextUser ?? undefined,
    // Guests have no /api/auth/me record — that fetch would 401-loop. The
    // synthesised guest user from AuthContext stands in via initialData.
    enabled: Boolean(contextUser) && contextUser?.isGuest !== true,
    staleTime: 5 * 60 * 1000,
  });
}
